/*
 * Mirrors the Umami design tokens in the @theme block in app/globals.css (the
 * canonical token source). When the tokens change there, update this file to match.
 * For inline styles, canvas, and Motion values only: prefer Tailwind utilities
 * (bg-bg-brand, text-fg-primary, ...) in className where possible.
 */

export const PALETTE = {
    green: {
        50: '#EEFEF4',
        100: '#D8FFE7',
        200: '#BAFAD4',
        300: '#79FCAD',
        400: '#53F293',
        500: '#3EE380',
        600: '#28CE6A',
        700: '#219750',
        800: '#145B32',
        900: '#11301D',
        950: '#08180F',
    },
    mustard: {
        50: '#FEFDE8',
        100: '#FFFBC2',
        200: '#FFF592',
        300: '#FFE645',
        400: '#FCD413',
        500: '#ECBB06',
        600: '#CC9102',
        700: '#A26706',
        800: '#86510D',
        900: '#724211',
        950: '#432205',
    },
    mango: {
        50: '#FFF8EB',
        100: '#FEEAC6',
        200: '#FFC86A',
        300: '#FFB64A',
        400: '#FF9B20',
        500: '#F97607',
        600: '#DD5202',
        700: '#B73506',
        800: '#94280C',
        900: '#7A220D',
        950: '#460E02',
    },
    strawberry: {
        50: '#FEF2F2',
        100: '#FEE2E2',
        200: '#FFC9C9',
        300: '#FDA4A4',
        400: '#FA6F6F',
        500: '#F24141',
        600: '#DF2323',
        700: '#BC1919',
        800: '#9B1919',
        900: '#811B1B',
        950: '#460909',
    },
    spirulina: {
        50: '#EFF8FF',
        100: '#DFF0FF',
        200: '#B8E3FF',
        300: '#78CDFF',
        400: '#24AFFF',
        500: '#069AF1',
        600: '#007ACE',
        700: '#0061A7',
        800: '#02528A',
        900: '#084572',
        950: '#062B4B',
    },
    grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#E5E5E5',
        300: '#D4D4D4',
        400: '#A3A3A3',
        500: '#737373',
        600: '#525252',
        700: '#404040',
        800: '#262626',
        900: '#171717',
        950: '#0A0A0A',
    },
}

export const COLORS = {
    /* Umami semantic tokens. Prefer these over palette entries. */
    bg: {
        neutralBase: '#FEFEFE',
        neutralSurface: PALETTE.grey[100],
        neutralSurfaceMedium: PALETTE.grey[200],
        neutralSurfaceStrong: PALETTE.grey[300],
        brand: PALETTE.green[400],
        brandStrong: PALETTE.green[500],
        brandSubtle: PALETTE.green[200],
        inverseBrand: PALETTE.green[900],
        inverseBrandStrong: PALETTE.green[950],
        inverseNeutral: PALETTE.grey[800],
        inverseNeutralSubtle: PALETTE.grey[700],
        accent01: PALETTE.mustard[200],
        accent01Strong: PALETTE.mustard[300],
        accent01Subtle: PALETTE.mustard[100],
        accent02: PALETTE.mango[200],
        accent03: PALETTE.spirulina[400],
        overlayBrand: '#53F29333',
        overlayBrandStrong: '#53F2934D',
        overlayBlackSubtle: '#0000000D',
        overlayBlack: '#0000001A',
        overlayWhite: '#FEFEFE33',
        overlayWhiteSubtle: '#FEFEFE0D',
    },
    fg: {
        primary: PALETTE.grey[950],
        secondary: '#000000B2',
        tertiary: PALETTE.grey[500],
        disabled: '#00000080',
        inverse: '#FEFEFE',
        inverseDisabled: '#FEFEFEB2',
        error: PALETTE.strawberry[500],
        brand: PALETTE.green[400],
        brandDisabled: '#53F29380',
    },
    border: {
        primary: '#0000001A',
        secondary: '#0000000D',
        strong: PALETTE.green[900],
        inversePrimary: '#FEFEFEB2',
        inverseSecondary: '#FEFEFE4D',
        inverseStrong: '#FEFEFE',
        error: PALETTE.strawberry[500],
        brand: '#53F29333',
    },

    /* Palette passthrough for existing COLORS.grey[x] style call sites. */
    grey: PALETTE.grey,
    mustard: PALETTE.mustard,

    /* Legacy tokens (pre Umami). Migrate usages to the tokens above,
       then remove. */
    trueBlack: '#000000',
    black: '#1c1d28',
    white: '#ffffff',
    neotaste: PALETTE.green[400],
    neotasteDark: PALETTE.green[900],

    background: 'hsl(var(--background-start-rgb))',
    foreground: 'hsl(var(--foreground-rgb))',

    warning: 'hsl(var(--warning))',
    'warning-foreground': 'hsl(var(--warning-foreground))',

    destructive: 'hsl(var(--destructive))',
    'destructive-foreground': 'hsl(var(--destructive-foreground))',

    blackTransparent100: 'rgba(28, 29, 40, 0)',
    blackTransparent20: 'rgba(28, 29, 40, 0.2)',
    blackTransparent10: 'rgba(28, 29, 40, 0.1)',
}
